import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  applyTurnSnapshot,
  clearActiveTurn,
  clearChatDraft,
  createResumableTurnReference,
  loadActiveTurn,
  loadChatDraft,
  saveActiveTurn,
  saveChatDraft,
  type ResumableChatApp,
  type ResumableTurnReference,
  type ResumableTurnSnapshot,
} from '@lightning/utils';

export interface UseResumableChatOptions {
  app: ResumableChatApp;
  surface: string;
  conversationId?: string | null;
  /** Disable when each conversation must retain its own independent draft. */
  migrateDraftOnConversationChange?: boolean;
}

export interface UseResumableChatResult {
  activeTurn: ResumableTurnReference | null;
  beginTurn: (conversationId?: string | null) => ResumableTurnReference;
  updateTurn: (updates: Partial<ResumableTurnReference>) => void;
  acceptSnapshot: (snapshot: ResumableTurnSnapshot) => void;
  finishTurn: () => void;
  draft: string;
  setDraft: (value: string) => void;
  clearDraft: () => void;
}

export function useResumableChat({
  app,
  surface,
  conversationId,
  migrateDraftOnConversationChange = true,
}: UseResumableChatOptions): UseResumableChatResult {
  const scope = useMemo(() => ({ app, surface }), [app, surface]);
  const [activeTurn, setActiveTurn] = useState<ResumableTurnReference | null>(() =>
    loadActiveTurn(app, surface),
  );
  const [draft, setDraftState] = useState(() =>
    loadChatDraft(app, surface, conversationId),
  );
  const draftConversationRef = useRef<string | null | undefined>(conversationId);

  useEffect(() => {
    setActiveTurn(loadActiveTurn(scope.app, scope.surface));
  }, [scope]);

  useEffect(() => {
    const previousConversationId = draftConversationRef.current;
    const storedDraft = loadChatDraft(scope.app, scope.surface, conversationId);
    setDraftState((currentDraft) => {
      if (storedDraft) return storedDraft;
      if (migrateDraftOnConversationChange && previousConversationId !== conversationId && currentDraft) {
        saveChatDraft(scope.app, scope.surface, conversationId, currentDraft);
        clearChatDraft(scope.app, scope.surface, previousConversationId);
        return currentDraft;
      }
      return '';
    });
    draftConversationRef.current = conversationId;
  }, [conversationId, scope, migrateDraftOnConversationChange]);

  const beginTurn = useCallback((nextConversationId?: string | null) => {
    const reference = createResumableTurnReference(
      scope.app,
      scope.surface,
      nextConversationId ?? conversationId,
    );
    saveActiveTurn(reference);
    setActiveTurn(reference);
    return reference;
  }, [conversationId, scope]);

  const updateTurn = useCallback((updates: Partial<ResumableTurnReference>) => {
    setActiveTurn((current) => {
      if (!current) return current;
      const next = { ...current, ...updates, updatedAt: new Date().toISOString() };
      saveActiveTurn(next);
      return next;
    });
  }, []);

  const acceptSnapshot = useCallback((snapshot: ResumableTurnSnapshot) => {
    setActiveTurn((current) => {
      if (!current) return current;
      const next = applyTurnSnapshot(current, snapshot);
      saveActiveTurn(next);
      return next;
    });
  }, []);

  const finishTurn = useCallback(() => {
    clearActiveTurn(scope.app, scope.surface);
    setActiveTurn(null);
  }, [scope]);

  const setDraft = useCallback((value: string) => {
    setDraftState(value);
    saveChatDraft(scope.app, scope.surface, conversationId, value);
  }, [conversationId, scope]);

  const clearDraftValue = useCallback(() => {
    clearChatDraft(scope.app, scope.surface, conversationId);
    setDraftState('');
  }, [conversationId, scope]);

  return {
    activeTurn,
    beginTurn,
    updateTurn,
    acceptSnapshot,
    finishTurn,
    draft,
    setDraft,
    clearDraft: clearDraftValue,
  };
}
